import lockupUrl from '../assets/boardpandas-lockup.png';
import markUrl from '../assets/boardpandas-mark.png';

/**
 * Inline SVG on a 12/16/20/24 grid. Never emoji, never a dingbat font — both render
 * differently per platform and neither is announced usefully by a screen reader.
 * Every icon here is decorative; the meaning is always carried by adjacent text.
 */

interface IconProps {
  size?: number;
  className?: string;
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

export function GiftIcon({ size = 12, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      {...stroke}
    >
      <rect x="3" y="8" width="18" height="13" rx="1" />
      <path d="M12 8v13M3 13h18" />
      <path d="M12 8S9.5 3.5 7 4.5 7.5 8 12 8zM12 8s2.5-4.5 5-3.5S16.5 8 12 8z" />
    </svg>
  );
}

export function CheckIcon({ size = 12, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * "Somebody is waiting on a word from you." A speech bubble rather than a bell or a warning
 * triangle: both attention reasons are a request for the client to say something, and neither
 * is an alarm — design-guardrails §4 is explicit that a client scanning the board reads alarm
 * shapes and colours as "something went wrong", which needing their input is not.
 */
export function AttentionIcon({ size = 12, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      {...stroke}
    >
      <path d="M21 12a8 8 0 0 1-8 8H4l2.5-3A8 8 0 1 1 21 12z" />
      <path d="M12 8v4" />
      <path d="M12 15.5h.01" />
    </svg>
  );
}

/**
 * "We asked you for something specific and cannot proceed without it."
 *
 * A circled question mark rather than a warning triangle, and the distinction is the same one
 * `AttentionIcon` makes: the ground under this card is the danger tint because that is what
 * was asked for, so the SHAPE is the place to keep design-guardrails §4's promise that a
 * client does not read the board as a list of things that went wrong. A question is a
 * question whatever colour it sits on.
 */
/**
 * An arrow curving back — a reply that has come in. Not a tick: a tick means finished, and the
 * client answering is the point at which work can START again.
 */
export function ReplyIcon({ size = 12, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      {...stroke}
    >
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10a6 6 0 0 1 6 6v5" />
    </svg>
  );
}

export function InfoNeededIcon({ size = 12, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      {...stroke}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.2 2.4c-.6.2-.95.75-.95 1.35v.5" />
      <path d="M12 16.5h.01" />
    </svg>
  );
}

export function LockIcon({ size = 12, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      {...stroke}
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function SyncIcon({ size = 12, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      {...stroke}
    >
      <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-7.35-3.8M3 12a9 9 0 0 1 9-9 9 9 0 0 1 7.35 3.8" />
      <path d="M21 3v5h-5M3 21v-5h5" />
    </svg>
  );
}

export function GithubIcon({ size = 13, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      fill="currentColor"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.5.5.09.66-.22.66-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.89 1.53 2.34 1.09 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02a9.5 9.5 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.69-4.57 4.94.36.31.68.92.68 1.85v2.74c0 .27.16.58.67.48A10 10 0 0 0 22 12c0-5.52-4.48-10-10-10z" />
    </svg>
  );
}

/**
 * The BoardPandas mark, from `BoardPandas/bp-website`.
 *
 * IMPORTED rather than referenced from `public/`. Vite fingerprints what it imports and serves
 * it immutable; a file in `public/` keeps a stable URL, and Cloudflare puts a four-hour browser
 * TTL on it whatever the origin's no-cache header says. A logo that changes would then be
 * invisible to returning visitors for four hours, which is exactly how this was found.
 *
 * A raster rather than an inline SVG: the brand ships the mark as artwork, not as a path set,
 * and redrawing it by hand is how a logo quietly stops being the logo. The file is the
 * organisation's own `bp-bear.png`, the same image the website's navbar uses.
 *
 * `alt=""` because the adjacent wordmark already names the product — announcing "BoardPandas"
 * twice is worse than not announcing the image at all.
 */
export function LogoMark({ size = 28, className }: { size?: number; className?: string }) {
  return (
    <img
      src={markUrl}
      alt=""
      width={size}
      // The source is 90x96, so height is derived rather than squared -- a square box would
      // squash it, and letting it size itself shifts the header as the image loads.
      height={Math.round((size * 96) / 90)}
      className={className}
      decoding="async"
    />
  );
}

/**
 * The full lockup — mark plus wordmark. Used where the product introduces itself.
 *
 * The file is the brand's `bp-normal-light.png` with its empty canvas trimmed off: the export
 * carries 139px of blank margin on each side of a 600px image, so a width set in CSS was
 * describing the box rather than the logo and the mark came out at little over half the size
 * asked for. Nothing about the artwork itself is changed.
 */
export function LogoLockup({ width = 176 }: { width?: number }) {
  return (
    <img
      src={lockupUrl}
      alt="BoardPandas"
      width={width}
      height={Math.round((width * 202) / 330)}
      decoding="async"
    />
  );
}
